import React, { useState, useEffect, useCallback, useRef } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './MultiStepInstr.css';

const IMAGES = {
  pyramid: 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary/pyramid_transparent.png',
  ball: 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary/ball_transparent.png',
  kubik_red: 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary/kubik_red.png',
  kubik_blue: 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary/kubik_blue.png',
  kubik_yellow: 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary/kubik_yellow.png',
  kubik_green: 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary/kubik_green.png',
};

const ItemImg = ({ src, alt }) => (
  <img src={src} alt={alt} style={{ width: '80px', height: '80px', objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }} draggable={false} />
);

const img = (key, alt) => <ItemImg src={IMAGES[key]} alt={alt} />;

const TRIALS = [
  {
    id: 1, instruction: 'Сначала положи красный кубик на ковёр. Потом синий мяч на пол рядом с ковром.', checkMode: 'sequence',
    grid: [
      { id: 'kubik_red', type: 'target', content: img('kubik_red', 'Красный кубик') },
      { id: 'ball', type: 'target', content: img('ball', 'Мячик') },
      { id: 'pyramid', type: 'excluded', content: img('pyramid', 'Пирамидка') },
      { id: 'kubik_yellow', type: 'excluded', content: img('kubik_yellow', 'Жёлтый кубик') },
      { id: 'kubik_green', type: 'excluded', content: img('kubik_green', 'Зелёный кубик') },
      { id: 'kubik_blue', type: 'excluded', content: img('kubik_blue', 'Синий кубик') },
    ],
    correctSteps: [{ zone: 'A', id: 'kubik_red' }, { zone: 'B', id: 'ball' }],
  },
  {
    id: 2, instruction: 'Положи жёлтый кубик на ковер, а зелёный на пол рядом с ковром.', checkMode: 'sequence',
    grid: [
      { id: 'kubik_yellow', type: 'target', content: img('kubik_yellow', 'Жёлтый кубик') },
      { id: 'kubik_green', type: 'target', content: img('kubik_green', 'Зелёный кубик') },
      { id: 'pyramid', type: 'excluded', content: img('pyramid', 'Пирамидка') },
      { id: 'ball', type: 'excluded', content: img('ball', 'Мячик') },
      { id: 'kubik_red', type: 'excluded', content: img('kubik_red', 'Красный кубик') },
      { id: 'kubik_blue', type: 'excluded', content: img('kubik_blue', 'Синий кубик') },
    ],
    correctSteps: [{ zone: 'A', id: 'kubik_yellow' }, { zone: 'B', id: 'kubik_green' }],
  },
  {
    id: 3, instruction: 'Перетащи на ковёр только кубики. Остальные игрушки не нужно трогать.', checkMode: 'set',
    grid: [
      { id: 'kubik_red', type: 'target', content: img('kubik_red', 'Красный кубик') },
      { id: 'kubik_blue', type: 'target', content: img('kubik_blue', 'Синий кубик') },
      { id: 'kubik_yellow', type: 'target', content: img('kubik_yellow', 'Жёлтый кубик') },
      { id: 'kubik_green', type: 'target', content: img('kubik_green', 'Зелёный кубик') },
      { id: 'pyramid', type: 'excluded', content: img('pyramid', 'Пирамидка') },
      { id: 'ball', type: 'excluded', content: img('ball', 'Мячик') },
    ],
    correctSteps: [{ zone: 'A', id: 'kubik_red' }, { zone: 'A', id: 'kubik_blue' }, { zone: 'A', id: 'kubik_yellow' }, { zone: 'A', id: 'kubik_green' }],
  },
  {
    id: 4, instruction: 'Сначала положи на ковёр пирамидку, потом мячик, а потом положи красный и жёлтый кубики на пол рядом с ковром.', checkMode: 'sequence',
    grid: [
      { id: 'pyramid', type: 'target', content: img('pyramid', 'Пирамидка') },
      { id: 'ball', type: 'target', content: img('ball', 'Мячик') },
      { id: 'kubik_red', type: 'target', content: img('kubik_red', 'Красный кубик') },
      { id: 'kubik_yellow', type: 'target', content: img('kubik_yellow', 'Жёлтый кубик') },
      { id: 'kubik_blue', type: 'excluded', content: img('kubik_blue', 'Синий кубик') },
      { id: 'kubik_green', type: 'excluded', content: img('kubik_green', 'Зелёный кубик') },
    ],
    correctSteps: [{ zone: 'A', id: 'pyramid' }, { zone: 'A', id: 'ball' }, { zone: 'B', id: 'kubik_red' }, { zone: 'B', id: 'kubik_yellow' }],
  },
];

const MultiStepInstr = ({ onBack, onComplete }) => {
  const { play: playInstruction } = useInstructionAudio();
  const [currentTrialIdx, setCurrentTrialIdx] = useState(0);
  const [gridItems, setGridItems] = useState([]);
  const [zoneA, setZoneA] = useState([]);
  const [zoneB, setZoneB] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);

  const gridRef = useRef([]);
  const zoneARef = useRef([]);
  const zoneBRef = useRef([]);
  const logRef = useRef([]);
  const attemptRef = useRef(1);

  const totalTrials = TRIALS.length;
  const currentTrial = TRIALS[currentTrialIdx];

  const speakText = useCallback((text) => {
    playInstruction(text);
  }, [playInstruction]);

  // The retry message is "Попробуй ещё раз и послушай внимательно." followed
  // by the trial's own instruction — both exist as separate DB clips, so
  // play them back-to-back instead of needing one combined recording.
  const speakRetry = useCallback((trialInstruction) => {
    playInstruction('Попробуй ещё раз и послушай внимательно.', () => {
      playInstruction(trialInstruction);
    });
  }, [playInstruction]);

  const repeatInstruction = () => {
    const trial = TRIALS[currentTrialIdx];
    if (trial?.instruction) speakText(trial.instruction);
  };

  const resetTrial = () => {
    const trial = TRIALS[currentTrialIdx];
    if (!trial) return;
    const shuffled = [...trial.grid].sort(() => Math.random() - 0.5);
    gridRef.current = shuffled; zoneARef.current = []; zoneBRef.current = []; logRef.current = [];
    setGridItems(shuffled); setZoneA([]); setZoneB([]); setFeedbackType(null);
  };

  useEffect(() => { resetTrial(); attemptRef.current = 1; }, [currentTrialIdx]);
  useEffect(() => { const trial = TRIALS[currentTrialIdx]; if (trial?.instruction) speakText(trial.instruction); }, [currentTrialIdx, speakText]);

  const hasMovedExcluded = (trial, zA, zB) => {
    const excludedIds = trial.grid.filter((i) => i.type === 'excluded').map((i) => i.id);
    return zA.some((zi) => excludedIds.includes(zi.id)) || zB.some((zi) => excludedIds.includes(zi.id));
  };

  const checkAnswer = (trial, log, zA, zB) => {
    if (hasMovedExcluded(trial, zA, zB)) return false;
    if (trial.checkMode === 'set') {
      return trial.correctSteps.every((step) => {
        if (step.zone === 'A') return zA.some((zi) => zi.id === step.id);
        if (step.zone === 'B') return zB.some((zi) => zi.id === step.id);
        return false;
      });
    } else {
      const targetIds = trial.grid.filter((i) => i.type === 'target').map((i) => i.id);
      const targetLog = log.filter((a) => targetIds.includes(a.id));
      return JSON.stringify(targetLog) === JSON.stringify(trial.correctSteps);
    }
  };

  const processResult = (isCorrect) => {
    setIsLocked(true); const trialIdx = currentTrialIdx; const currentAttempt = attemptRef.current;
    if (isCorrect) {
      setFeedbackType('success'); setAnswers((prev) => [...prev, { correct: true, attempt: currentAttempt }]);
      setTimeout(() => { if (trialIdx < totalTrials - 1) setCurrentTrialIdx(trialIdx + 1); else setShowResult(true); setIsLocked(false); }, 1200);
    } else {
      setFeedbackType('error');
      if (currentAttempt < 2) {
        setTimeout(() => { attemptRef.current = 2; resetTrial(); setIsLocked(false); speakRetry(TRIALS[trialIdx].instruction); }, 1500);
      } else {
        setAnswers((prev) => [...prev, { correct: false, attempt: 2 }]);
        setTimeout(() => { if (trialIdx < totalTrials - 1) setCurrentTrialIdx(trialIdx + 1); else setShowResult(true); setIsLocked(false); }, 1500);
      }
    }
  };

  const handleDragStart = (e, id) => { if (isLocked) { e.preventDefault(); return; } e.dataTransfer.setData('text/plain', id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const handleDropToZone = (zone, e) => {
    e.preventDefault(); if (isLocked) return;
    const id = e.dataTransfer.getData('text/plain'); if (!id) return;
    const itemToMove = gridRef.current.find((i) => i.id === id); if (!itemToMove) return;
    
    gridRef.current = gridRef.current.filter((i) => i.id !== id);
    if (zone === 'A') zoneARef.current = [...zoneARef.current, itemToMove];
    else zoneBRef.current = [...zoneBRef.current, itemToMove];
    logRef.current = [...logRef.current, { zone, id }];

    setGridItems([...gridRef.current]); setZoneA([...zoneARef.current]); setZoneB([...zoneBRef.current]);

    const trial = TRIALS[currentTrialIdx];
    const targetItems = trial.grid.filter((i) => i.type === 'target');
    const allTargetsMoved = targetItems.every((item) => !gridRef.current.find((g) => g.id === item.id));
    if (allTargetsMoved && targetItems.length > 0) {
      setTimeout(() => { const isCorrect = checkAnswer(trial, logRef.current, zoneARef.current, zoneBRef.current); processResult(isCorrect); }, 400);
    }
  };

  useEffect(() => {
    if (!showResult) return;
    const timer = setTimeout(() => {
      const correctCount = answers.filter((a) => a.correct).length; const score = Math.round((correctCount / totalTrials) * 100);
      const trialsDetail = answers.map((a, i) => ({ trialId: TRIALS[i].id, instruction: TRIALS[i].instruction, correct: a.correct, attempt: a.attempt, objectsCount: TRIALS[i].correctSteps.length }));
      if (onComplete) { onComplete({ testId: 'MultiStepInstr', score, details: { total: totalTrials, correct: correctCount, trials: trialsDetail } }); } else { onBack(); }
    }, 2000);
    return () => clearTimeout(timer);
  }, [showResult, answers, totalTrials, onComplete, onBack]);

  if (showResult) {
    const correctCount = answers.filter((a) => a.correct).length;
    return (
      <ModuleTemplate progress={answers.map((a) => a.correct)} totalQuestions={totalTrials} forceStop={true} onComplete={onComplete || onBack}>
        <div className="mi-result-content">
          <h2 className="mi-result-title">Упражнение завершено!</h2>
          <div className="mi-result-score">
            <span className="mi-score-number">{correctCount}</span>
            <span className="mi-score-total"> / {totalTrials}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentTrial) return (
    <ModuleTemplate progress={[]} totalQuestions={1} forceStop={true} onComplete={onComplete || onBack}>
      <div>Загрузка...</div>
    </ModuleTemplate>
  );

  const floorFeedbackStyle = feedbackType === 'success' ? { border: '3px solid #22C55E', background: 'rgba(34,197,94,0.08)' } : feedbackType === 'error' ? { border: '3px solid #EF4444', background: 'rgba(239,68,68,0.08)' } : {};

  return (
    <ModuleTemplate progress={answers.map((a) => a.correct)} totalQuestions={totalTrials} forceStop={true} onComplete={onComplete || onBack} onRepeat={repeatInstruction}>
      <div className="mi-container">
        <div className="mi-grid">
          {gridItems.map((item) => (
            <div key={item.id} className="mi-grid-cell" draggable={!isLocked} onDragStart={(e) => handleDragStart(e, item.id)}>{item.content}</div>
          ))}
        </div>
        <div className="mi-floor" onDragOver={handleDragOver} onDrop={(e) => handleDropToZone('B', e)} style={{ ...floorFeedbackStyle, borderRadius: '16px', transition: 'all 0.3s ease' }}>
          <div className="mi-rug" onDragOver={handleDragOver} onDrop={(e) => { e.stopPropagation(); handleDropToZone('A', e); }}>
            <div className="mi-rug-items">{zoneA.map((item) => (<div key={item.id} className="mi-zone-item">{item.content}</div>))}</div>
          </div>
          <div className="mi-floor-items">{zoneB.map((item) => (<div key={item.id} className="mi-zone-item">{item.content}</div>))}</div>
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default MultiStepInstr;